import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { User, Loader2, Phone, Mail, MapPin } from "lucide-react";
import { useMinisterPhoto } from "../../hooks/useMinisterPhoto";
import type { Minister } from "@/models/minister";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";
import type { Degree } from "@/models/degree";

interface ViewMinisterDialogProps {
  minister: Minister | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  districts: District[];
  regions: Region[];
  degrees: Degree[];
}

/**
 * Molecule: View Minister Dialog
 * Shows minister information in read-only mode with photo and comprehensive info card
 */
export function ViewMinisterDialog({
  minister,
  open,
  onOpenChange,
}: ViewMinisterDialogProps) {
  const { photoUrl, isLoading: isPhotoLoading } = useMinisterPhoto(minister?.profile_picture_filename);

  if (!minister) return null;

  // Generate initials for fallback
  const initials = `${minister.first_name?.charAt(0) || ''}${minister.last_name?.charAt(0) || ''}`.toUpperCase();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Información del Ministro</DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col gap-6 sm:flex-row">
          {/* Photo Section - ~30-40% width */}
          <div className="flex w-full shrink-0 flex-col items-center sm:w-64">
            <div className="h-72 w-60">
              {isPhotoLoading ? (
                <div className="flex h-72 w-60 items-center justify-center rounded-md bg-muted">
                  <Loader2 className="size-12 animate-spin text-muted-foreground" />
                </div>
              ) : photoUrl ? (
                <img 
                  src={photoUrl} 
                  alt="Foto de perfil" 
                  className="h-72 w-60 rounded-md object-cover"
                />
              ) : (
                <div className="flex h-72 w-60 items-center justify-center rounded-md bg-muted">
                  {initials ? (
                    <span className="text-6xl font-semibold text-muted-foreground">
                      {initials}
                    </span>
                  ) : (
                    <User className="size-24 text-muted-foreground" />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Info Card Section - ~60-70% width */}
          <Card className="flex-1 bg-muted/30">
            <div className="space-y-4 p-6">
              {/* Full Name - Prominent */}
              <div className="mb-4">
                <p className="text-lg font-semibold">{minister.full_name}</p>
              </div>

              {/* Personal Information Section */}
              <div className="space-y-3">
                <div className="rounded bg-muted px-3 py-2">
                  <h3 className="text-sm font-semibold">Información Personal</h3>
                </div>
                
                {minister.adb_code && (
                  <div>
                    <p className="text-sm text-muted-foreground">Código ADB</p>
                    <p className="text-base font-medium">{minister.adb_code}</p>
                  </div>
                )}

                {minister.dni && (
                  <div>
                    <p className="text-sm text-muted-foreground">Cédula de Identidad</p>
                    <p className="text-base font-medium">{minister.dni}</p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-muted-foreground">Estado</p>
                  <Badge variant={minister.is_active ? "default" : "secondary"}>
                    {minister.is_active ? "Activo" : "Inactivo"}
                  </Badge>
                </div>

                {minister.degree && (
                  <div>
                    <p className="text-sm text-muted-foreground">Grado Ministerial</p>
                    <p className="text-base font-medium">{minister.degree.name}</p>
                  </div>
                )}

                {minister.position && (
                  <div>
                    <p className="text-sm text-muted-foreground">Cargo</p>
                    <p className="text-base font-medium">{minister.position}</p>
                  </div>
                )}
              </div>

              {/* Location Section */}
              <div className="space-y-3">
                <div className="rounded bg-muted px-3 py-2">
                  <h3 className="text-sm font-semibold">Ubicación</h3>
                </div>
                
                {minister.district && (
                  <div>
                    <p className="text-sm text-muted-foreground">Distrito</p>
                    <p className="text-base font-medium">{minister.district.name}</p>
                  </div>
                )}

                {minister.region && (
                  <div>
                    <p className="text-sm text-muted-foreground">Región</p>
                    <p className="text-base font-medium">{minister.region.name}</p>
                  </div>
                )}

                {(minister.address || minister.address_city || minister.address_zone) && (
                  <div>
                    <p className="text-sm text-muted-foreground">Dirección</p>
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-1 size-4 shrink-0 text-muted-foreground" />
                      <div className="text-base">
                        {minister.address && <p>{minister.address}</p>}
                        {(minister.address_city || minister.address_zone) && (
                          <p className="text-sm text-muted-foreground">
                            {[minister.address_zone, minister.address_city].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Contact Information Section */}
              {(minister.cell_phone || minister.office_phone || minister.email) && (
                <div className="space-y-3">
                  <div className="rounded bg-muted px-3 py-2">
                    <h3 className="text-sm font-semibold">Información de Contacto</h3>
                  </div>
                  
                  {minister.cell_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="size-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Teléfono Celular</p>
                        <p className="text-base font-medium">{minister.cell_phone}</p>
                      </div>
                    </div>
                  )}

                  {minister.office_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="size-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Teléfono Oficina</p>
                        <p className="text-base font-medium">{minister.office_phone}</p>
                      </div>
                    </div>
                  )}

                  {minister.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="size-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="break-all text-base font-medium">{minister.email}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
